import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {arrayToggle} from '../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {FilterLabel} from '../../../../components/common/filters/common/filterLabel';
import {FilterRow} from '../../../../components/common/filters/common/types';
import {FilterDropdownBox} from '../../../../components/common/filters/filterDropdownBox/filterDropdownBox';
import {MultiSelectFilterDropdown} from '../../../../components/common/filters/multiSelectFilter/multiSelectFilterDropdown';
import {FutureTimeValuesEnum} from '../../../../models/api/futureTimeValueModel';
import {SettingsPreferenceItemInheritableProps} from '../../settingsPreferencesList';

/*
 * Props.
 */

interface SendLaterTimingsDropdownProps
  extends SettingsPreferenceItemInheritableProps<ReadonlyArray<FutureTimeValuesEnum>> {
  selectedTimings: ReadonlyArray<FutureTimeValuesEnum>;
}

/*
 * Component.
 */

export const SendLaterTimingsDropdown: FC<SendLaterTimingsDropdownProps> = (props) => {
  const {selectedTimings, updatePreference} = props;

  return (
    <FilterDropdownBox
      renderButton={() => (
        <FilterLabel>
          <Trans id="select">Select</Trans>
        </FilterLabel>
      )}
      renderDropdown={(popoverProps) => (
        <MultiSelectFilterDropdown
          {...popoverProps}
          title={props.title}
          rows={renderItems(selectedTimings, updatePreference)}
          isMultiSelect
          showClearButton={false}
          onClear={() => {}}
        />
      )}
    />
  );
};

function renderItems(
  selectedItems: ReadonlyArray<FutureTimeValuesEnum>,
  updatePreference: HandlerOf<ReadonlyArray<FutureTimeValuesEnum>>,
): ReadonlyArray<FilterRow> {
  const onItemSelect = (item: FutureTimesDropdownItem) => {
    updatePreference(arrayToggle(selectedItems, item.key));
  };
  return FUTURE_TIMES_DROPDOWN_ITEMS.map((item) => ({
    renderer: () => <DropdownItemContent>{item.label}</DropdownItemContent>,
    isSelected: selectedItems.includes(item.key),
    onSelect: () => onItemSelect(item),
  }));
}

/*
 * Constant.
 */

interface FutureTimesDropdownItem {
  key: FutureTimeValuesEnum;
  label: ReactNode;
}

const FUTURE_TIMES_DROPDOWN_ITEMS: ReadonlyArray<FutureTimesDropdownItem> = [
  {
    key: FutureTimeValuesEnum.ADD_2_MINUTES,
    label: <Trans id="teammate-prefs-send-later-2min">In 2 minutes</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_10_MINUTES,
    label: <Trans id="teammate-prefs-send-later-10min">In 10 minutes</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_20_MINUTES,
    label: <Trans id="teammate-prefs-send-later-20min">In 20 minutes</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_30_MINUTES,
    label: <Trans id="teammate-prefs-send-later-30min">In 30 minutes</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_40_MINUTES,
    label: <Trans id="teammate-prefs-send-later-40min">In 40 minutes</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_50_MINUTES,
    label: <Trans id="teammate-prefs-send-later-50min">In 50 minutes</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_1_HOUR,
    label: <Trans id="teammate-prefs-send-later-1hr">In 1 hour</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_1_HOUR_HALF,
    label: <Trans id="teammate-prefs-send-later-1.5hr">In 1.5 hours</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_2_HOURS,
    label: <Trans id="teammate-prefs-send-later-2hr">In 2 hours</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_3_HOURS,
    label: <Trans id="teammate-prefs-send-later-3hr">In 3 hours</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_4_HOURS,
    label: <Trans id="teammate-prefs-send-later-4hr">In 4 hours</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_5_HOURS,
    label: <Trans id="teammate-prefs-send-later-5hr">In 5 hours</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_6_HOURS,
    label: <Trans id="teammate-prefs-send-later-6hr">In 6 hours</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_8_HOURS,
    label: <Trans id="teammate-prefs-send-later-8hr">In 8 hours</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_10_HOURS,
    label: <Trans id="teammate-prefs-send-later-10hr">In 10 hours</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_12_HOURS,
    label: <Trans id="teammate-prefs-send-later-12hr">In 12 hours</Trans>,
  },
  {
    key: FutureTimeValuesEnum.TOMORROW_9AM,
    label: <Trans id="teammate-prefs-send-later-tmrw9">Tomorrow 9am</Trans>,
  },
  {
    key: FutureTimeValuesEnum.TOMORROW_2PM,
    label: <Trans id="teammate-prefs-send-later-tmrw2">Tomorrow 2pm</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_2_DAYS,
    label: <Trans id="teammate-prefs-send-later-2day">In 2 days</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_3_DAYS,
    label: <Trans id="teammate-prefs-send-later-3day">In 3 days</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_4_DAYS,
    label: <Trans id="teammate-prefs-send-later-4day">In 4 days</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_5_DAYS,
    label: <Trans id="teammate-prefs-send-later-5day">In 5 days</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_6_DAYS,
    label: <Trans id="teammate-prefs-send-later-6day">In 6 days</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_1_WEEK,
    label: <Trans id="teammate-prefs-send-later-1wk">In 1 week</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_10_DAYS,
    label: <Trans id="teammate-prefs-send-later-10day">In 10 days</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_2_WEEKS,
    label: <Trans id="teammate-prefs-send-later-2wk">In 2 weeks</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_3_WEEKS,
    label: <Trans id="teammate-prefs-send-later-3wk">In 3 weeks</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_1_MONTH,
    label: <Trans id="teammate-prefs-send-later-1mth">In 1 month</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_2_MONTHS,
    label: <Trans id="teammate-prefs-send-later-2mth">In 2 months</Trans>,
  },
  {
    key: FutureTimeValuesEnum.ADD_3_MONTHS,
    label: <Trans id="teammate-prefs-send-later-3mth">In 3 months</Trans>,
  },
];
